import React, { useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileSearch, ChevronRight, Building, User, Phone, Mail,
  TreePine, Car, FileCheck, Shield, Hammer, MessageSquare,
  Layers, ExternalLink, Copy, Check, ChevronDown
} from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';

// ═══════════════════════════════════════════════════════════
// EXTRACTION HELPERS
// ═══════════════════════════════════════════════════════════

const INVALID_VALUE = /^(?:n\/a|na|none|not provided|not found|null|nil|unknown|\(none\)|-+|\.+)$/i;

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

const isUsable = (v) => {
  const c = clean(v);
  return c.length > 0 && !INVALID_VALUE.test(c);
};

const normalizeKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const isInternalSignalKey = (value) => {
  const normalized = normalizeKey(value);
  if (!normalized) return true;
  if (normalized.startsWith('_')) return true;
  if (/(^|_)all_fields(_|$)/.test(normalized)) return true;
  if (/(^|_)parser(_|$)/.test(normalized)) return true;
  if (/(^|_)qa_warnings(_|$)/.test(normalized)) return true;
  return false;
};

const getFieldKeyCandidates = (field) => {
  if (!field || typeof field !== 'object') return [];
  return [field.field_key_base, field.field_key, field.key]
    .map(normalizeKey)
    .filter((key) => key && !isInternalSignalKey(key));
};

const keyMatchesTarget = (fieldKey, targetKey) => {
  if (!fieldKey || !targetKey) return false;
  if (fieldKey === targetKey) return true;
  if (fieldKey.startsWith(`${targetKey}_`)) return true;
  if (fieldKey.endsWith(`_${targetKey}`)) return true;
  if (fieldKey.includes(`_${targetKey}_`)) return true;
  return false;
};

/**
 * Extract a single insight from form fields by field_key_base.
 * Returns { value, page, question, confidence } or null.
 */
export const extractInsight = (fields, fieldKeyBase, options = {}) => {
  if (!Array.isArray(fields) || !fieldKeyBase) return null;

  const aliases = Array.isArray(options.aliases) ? options.aliases : [];
  const targets = [fieldKeyBase, ...aliases].map(normalizeKey).filter(Boolean);
  if (!targets.length) return null;

  for (const field of fields) {
    if (!field || typeof field !== 'object') continue;
    const fieldKeys = getFieldKeyCandidates(field);
    if (!fieldKeys.length) continue;
    const isKeyMatch = fieldKeys.some((fk) => targets.some((target) => keyMatchesTarget(fk, target)));
    if (!isKeyMatch) continue;

    // For checkbox fields, only consider checked ones or non-checkbox fields
    if (field.is_checkbox_option && !field.is_checked) continue;

    const value = clean(field.answer_normalized || field.answer_raw || field.value || '');
    if (!isUsable(value)) continue;

    return {
      value,
      page: field.page ?? null,
      question: clean(field.question_contextual || field.question || field.label || ''),
      confidence: field.confidence ?? null,
      fieldKeyBase: fieldKeyBase,
    };
  }
  return null;
};

/**
 * Extract a checkbox group result - finds which option is checked.
 * Returns { value, page, question, allOptions } or null.
 */
export const extractCheckboxGroup = (fields, fieldKeyBase, options = {}) => {
  if (!Array.isArray(fields) || !fieldKeyBase) return null;

  const aliases = Array.isArray(options.aliases) ? options.aliases : [];
  const targets = [fieldKeyBase, ...aliases].map(normalizeKey).filter(Boolean);
  if (!targets.length) return null;
  const groupFields = [];

  for (const field of fields) {
    if (!field || typeof field !== 'object') continue;
    const fieldKeys = getFieldKeyCandidates(field);
    if (!fieldKeys.length) continue;
    const isKeyMatch = fieldKeys.some((fk) => targets.some((target) => keyMatchesTarget(fk, target)));
    if (!isKeyMatch) continue;
    if (field.is_checkbox_option) groupFields.push(field);
  }

  if (groupFields.length === 0) {
    // Fall back to extractInsight for non-checkbox fields
    return extractInsight(fields, fieldKeyBase, { aliases });
  }

  const checked = groupFields.filter((f) => f.is_checked);
  const allOptions = groupFields.map((f) => clean(f.answer_normalized || f.answer_raw || f.value || ''));

  if (checked.length === 0) return null;

  // Take the first checked value (usually one in yes/no groups)
  const primary = checked[0];
  const value = clean(primary.answer_normalized || primary.answer_raw || primary.value || '');

  if (!isUsable(value)) return null;

  return {
    value,
    page: primary.page ?? null,
    question: clean(primary.question_contextual || primary.question || primary.label || ''),
    confidence: primary.confidence ?? null,
    fieldKeyBase,
    allOptions,
  };
};


// ═══════════════════════════════════════════════════════════
// SIGNAL CONFIGURATION
// ═══════════════════════════════════════════════════════════

const KEY_SIGNALS = [
  {
    id: 'work-started',
    label: 'Work started',
    fieldKeyBase: 'description_of_proposal.has_the_work_already_been_started_and_or_completed',
    fallbackKeys: ['work_started', 'development_started', 'commencement_started'],
    icon: Hammer,
    notable: (v) => v && !/^no$/i.test(v),
    // Glance label: short text for the pill row
    glanceLabel: (v) => {
      if (!v) return null;
      const lower = v.toLowerCase();
      if (lower.includes('completed')) return 'Work completed';
      if (lower.includes('started') || /^yes/i.test(lower)) return 'Work started';
      return 'Work not started';
    },
    glanceNotable: (v) => v && !/^no$/i.test(v),
    format: (v) => {
      if (!v) return null;
      const lower = v.toLowerCase();
      if (lower.includes('completed')) return 'Yes — completed';
      if (lower.includes('started') || /^yes/i.test(lower)) return 'Yes — started';
      if (/^no$/i.test(lower)) return 'No';
      return null;
    },
  },
  {
    id: 'pre-app-discussion',
    label: 'Pre-app',
    fieldKeyBase: 'pre_application_discussion.have_you_discussed_your_proposal_with_the_planning_authority',
    fallbackKeys: ['pre_application_discussion'],
    icon: MessageSquare,
    notable: (v) => v && /^yes$/i.test(v),
    glanceLabel: (v) => /^yes$/i.test(v) ? 'Pre-app held' : 'No pre-app',
    glanceNotable: (v) => v && /^yes$/i.test(v),
    format: (v) => /^yes$/i.test(v) ? 'Yes' : /^no$/i.test(v) ? 'No' : null,
  },
  {
    id: 'trees',
    label: 'Trees',
    fieldKeyBase: 'trees.are_there_any_trees_on_or_adjacent_to_the_application_site',
    fallbackKeys: ['trees_on_site'],
    icon: TreePine,
    notable: (v) => v && /^yes$/i.test(v),
    glanceLabel: (v) => /^yes$/i.test(v) ? 'Trees present' : 'No trees',
    glanceNotable: (v) => v && /^yes$/i.test(v),
    format: (v) => /^yes$/i.test(v) ? 'Yes' : /^no$/i.test(v) ? 'No' : null,
  },
  {
    id: 'vehicle-access',
    label: 'Vehicle access',
    fieldKeyBase: 'access_and_parking.are_you_proposing_a_new_or_altered_vehicle_access_to_or_from_a_public_road',
    fallbackKeys: ['access_parking', 'vehicle_access'],
    icon: Car,
    notable: (v) => v && /^yes$/i.test(v),
    glanceLabel: (v) => /^yes$/i.test(v) ? 'Access changes' : 'No access changes',
    glanceNotable: (v) => v && /^yes$/i.test(v),
    format: (v) => /^yes$/i.test(v) ? 'Yes' : /^no$/i.test(v) ? 'No' : null,
  },
  {
    id: 'certificate',
    label: 'Certificate',
    fieldKeyBase: 'ownership_details.the_following_land_ownership_certificate_is_required_to_complete_this_section_of_the_proposal',
    fallbackKeys: ['ownership_certificate'],
    icon: FileCheck,
    notable: (v) => v && !/^a$/i.test(v),
    glanceLabel: (v) => {
      if (!v) return null;
      const m = v.match(/\b([A-D])\b/i);
      return m ? `Certificate ${m[1].toUpperCase()}` : null;
    },
    glanceNotable: (v) => v && !/^a$/i.test(v),
    format: (v) => {
      if (!v) return null;
      const m = v.match(/\b([A-D])\b/i);
      return m ? m[1].toUpperCase() : null;
    },
  },
  {
    id: 'sole-owner',
    label: 'Sole owner',
    fieldKeyBase: 'ownership_details.are_you_the_applicant_the_sole_owner_of_all_the_land',
    fallbackKeys: ['ownership_all_land'],
    icon: Shield,
    notable: (v) => v && /^no$/i.test(v),
    glanceLabel: (v) => /^no$/i.test(v) ? 'Not sole owner' : 'Sole owner',
    glanceNotable: (v) => v && /^no$/i.test(v),
    format: (v) => /^yes$/i.test(v) ? 'Yes' : /^no$/i.test(v) ? 'No' : null,
  },
];

const PLAN_FIELDS = [
  { key: 'existing_and_proposed_elevations', label: 'Elevations' },
  { key: 'existing_and_proposed_floor_plans', label: 'Floor plans' },
  { key: 'cross_sections', label: 'Cross sections' },
  { key: 'site_layout_plan_block_plans_including_access', label: 'Site layout' },
  { key: 'roof_plan', label: 'Roof plan' },
  { key: 'photographs_and_or_photomontages', label: 'Photographs' },
];

const PLAN_CHIP_THEMES = {
  'Elevations': {
    bg: 'rgba(118, 61, 49, 0.32)', // burnt red
    border: 'rgba(185, 108, 86, 0.28)',
    textClass: 'text-[#f2c7b8]',
    iconClass: 'text-[#dd9f89]',
  },
  'Floor plans': {
    bg: 'rgba(52, 76, 58, 0.34)', // charred green
    border: 'rgba(101, 141, 112, 0.27)',
    textClass: 'text-[#cee3d3]',
    iconClass: 'text-[#a8c9af]',
  },
  'Cross sections': {
    bg: 'rgba(49, 67, 74, 0.34)', // soot teal
    border: 'rgba(88, 120, 132, 0.27)',
    textClass: 'text-[#c8dce3]',
    iconClass: 'text-[#9fc0cb]',
  },
  'Site layout': {
    bg: 'rgba(90, 70, 40, 0.34)', // ember amber
    border: 'rgba(145, 114, 66, 0.27)',
    textClass: 'text-[#e6d3b5]',
    iconClass: 'text-[#d3b37f]',
  },
  'Roof plan': {
    bg: 'rgba(60, 64, 82, 0.34)', // smoked slate
    border: 'rgba(106, 113, 146, 0.27)',
    textClass: 'text-[#d2d7eb]',
    iconClass: 'text-[#b2badf]',
  },
  'Photographs': {
    bg: 'rgba(88, 53, 66, 0.34)', // faded mulberry
    border: 'rgba(137, 86, 105, 0.27)',
    textClass: 'text-[#e2c8d2]',
    iconClass: 'text-[#caa5b5]',
  },
};

const DEFAULT_PLAN_CHIP_THEME = {
  bg: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.05)',
  textClass: 'text-zinc-300/85',
  iconClass: 'text-zinc-400/80',
};

const getPlanChipTheme = (planLabel) => PLAN_CHIP_THEMES[planLabel] || DEFAULT_PLAN_CHIP_THEME;


// ═══════════════════════════════════════════════════════════
// CONTACT EXTRACTION (scoped - never leaks cross-role data)
// ═══════════════════════════════════════════════════════════

const extractContact = (fields, contextPrefix) => {
  if (!Array.isArray(fields)) return null;

  const prefix = contextPrefix.toLowerCase();
  const scoped = fields.filter((f) => {
    const ctx = String(f?.context_id || f?.context_id_raw || f?.context_label || f?.group || '').toLowerCase();
    return ctx.includes(prefix);
  });

  const roleFields = scoped.length > 0
    ? scoped
    : fields.filter((f) => {
      const key = normalizeKey(f?.field_key_base || f?.field_key || f?.key || '');
      const question = normalizeKey(f?.question_contextual || f?.question || f?.label || '');
      return key.startsWith(`${prefix}_`) || key.includes(`_${prefix}_`) || question.includes(prefix);
    });

  if (roleFields.length === 0) return null;

  const find = (tokens) => {
    for (const f of roleFields) {
      const key = String(f?.field_key_base || f?.field_key || f?.key || '').toLowerCase();
      const q = String(f?.question_contextual || f?.question || f?.label || '').toLowerCase();
      const haystack = key + ' ' + q;
      if (tokens.some((t) => haystack.includes(t))) {
        const val = clean(f?.answer_normalized || f?.answer_raw || f?.value || '');
        if (isUsable(val)) return val;
      }
    }
    return null;
  };

  const name = find(['first_name', 'firstname', 'name', 'surname', 'last_name']);
  const company = find(['company', 'organisation', 'organization', 'corporate', 'org']);
  const email = find(['email']);
  const phone = find(['telephone', 'phone', 'mobile', 'tel']);

  if (!name && !company && !email) return null;

  return { name, company, email, phone };
};


// ═══════════════════════════════════════════════════════════
// PLANS EXTRACTION
// ═══════════════════════════════════════════════════════════

const extractPlans = (fields) => {
  if (!Array.isArray(fields)) return [];

  const found = [];
  for (const plan of PLAN_FIELDS) {
    const normalizedKey = plan.key.toLowerCase();
    for (const field of fields) {
      const fk = String(field?.field_key_base || field?.field_key || field?.key || '').toLowerCase();
      if (!fk.includes(normalizedKey) && !fk.includes('checklist_householder')) continue;
      if (!fk.includes(normalizedKey)) continue;
      if (field.is_checkbox_option && !field.is_checked) continue;
      if (field.is_checked || isUsable(field?.answer_normalized || field?.answer_raw || field?.value)) {
        found.push(plan.label);
        break;
      }
    }
  }
  return found;
};


// ═══════════════════════════════════════════════════════════
// AT A GLANCE — structured pill row
// ═══════════════════════════════════════════════════════════

const isYesValue = (value) => /^yes\b/i.test(clean(value));

const parseCertificate = (value) => {
  const match = clean(value).match(/\b([A-D])\b/i);
  return match ? match[1].toUpperCase() : null;
};

const TEXT_TIER = {
  primary: 'text-[var(--c-text-primary)] font-medium',
  secondary: 'text-[var(--c-text-secondary)]',
  muted: 'text-[var(--c-text-secondary)]/80',
};

const buildNotableFlags = (signals) => {
  const byId = new Map(signals.map((signal) => [signal.id, signal]));
  const flags = [];

  const addFlag = (signalId, label, value) => {
    const signal = byId.get(signalId);
    if (!signal) return;
    flags.push({
      id: signal.id,
      label,
      value,
      fieldKeyBase: signal.fieldKeyBase,
      page: signal.page,
      question: signal.question,
    });
  };

  const workStarted = byId.get('work-started');
  if (workStarted?.raw) {
    const normalized = clean(workStarted.raw).toLowerCase();
    if (normalized.includes('completed')) {
      addFlag('work-started', 'Work started', 'Completed');
    } else if (normalized.includes('started') || /^yes\b/.test(normalized)) {
      addFlag('work-started', 'Work started', 'Started');
    }
  }

  const trees = byId.get('trees');
  if (trees?.raw && isYesValue(trees.raw)) addFlag('trees', 'Trees', 'Yes');

  const vehicleAccess = byId.get('vehicle-access');
  if (vehicleAccess?.raw && isYesValue(vehicleAccess.raw)) {
    addFlag('vehicle-access', 'Vehicle access altered', 'Yes');
  }

  const certificate = byId.get('certificate');
  const certCode = parseCertificate(certificate?.displayValue || certificate?.raw || '');
  if (certCode && certCode !== 'A') addFlag('certificate', 'Certificate', certCode);

  const preApp = byId.get('pre-app-discussion');
  if (preApp?.raw && isYesValue(preApp.raw)) addFlag('pre-app-discussion', 'Pre-app discussion', 'Yes');

  return flags;
};

const buildKeyInsights = (signals, plans, submittedByAgent) => {
  const byId = new Map(signals.map((signal) => [signal.id, signal]));
  const insights = [];

  insights.push(submittedByAgent ? 'Submitted by an agent on behalf of the applicant.' : 'Submitted directly by the applicant.');

  const workStarted = byId.get('work-started');
  if (workStarted?.displayValue) {
    const value = clean(workStarted.displayValue).replace(/^Yes\s*[—-]\s*/i, '');
    insights.push(/^no$/i.test(clean(workStarted.displayValue)) ? 'Work has not started.' : `Work status: ${value}.`);
  }

  const certificate = byId.get('certificate');
  if (certificate?.displayValue) {
    insights.push(`Ownership certificate: ${certificate.displayValue}.`);
  }

  if (Array.isArray(plans) && plans.length > 0) {
    insights.push(`${plans.length} supporting plan${plans.length === 1 ? '' : 's'} included.`);
  }

  const trees = byId.get('trees');
  if (trees?.raw && isYesValue(trees.raw)) insights.push('Trees are noted on or adjacent to the site.');

  const access = byId.get('vehicle-access');
  if (access?.raw && isYesValue(access.raw)) insights.push('Vehicle access alterations are proposed.');

  const preApp = byId.get('pre-app-discussion');
  if (preApp?.raw && isYesValue(preApp.raw)) insights.push('A pre-application discussion was held with the authority.');

  const soleOwner = byId.get('sole-owner');
  if (soleOwner?.raw && /^no$/i.test(clean(soleOwner.raw))) insights.push('The applicant is not the sole owner of all land.');

  return insights.slice(0, 4);
};


// ═══════════════════════════════════════════════════════════
// TOOLTIP WRAPPER — always renders, keyboard accessible
// ═══════════════════════════════════════════════════════════

const truncateQuestion = (q, max = 80) => {
  if (!q || q.length <= max) return q;
  return q.slice(0, max - 1).trimEnd() + '\u2026';
};

const ChipTooltip = ({ children, page, question }) => (
  <Tooltip.Root delayDuration={180}>
    <Tooltip.Trigger asChild>
      {children}
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        className="z-[9999] max-w-[260px] px-2.5 py-1.5 rounded-md text-[11px] leading-4 shadow-lg"
        style={{
          background: 'rgba(20,20,24,0.96)',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 10px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)',
        }}
        sideOffset={5}
        side="top"
      >
        {question && (
          <p className={`${TEXT_TIER.primary} mb-0.5 text-[11px] leading-4`}>{truncateQuestion(question)}</p>
        )}
        <p className={`font-mono text-[10px] leading-4 ${TEXT_TIER.muted}`}>
          {page != null ? `Source: Page ${page}` : 'Source: Form'}
        </p>
        <Tooltip.Arrow style={{ fill: 'rgba(255,255,255,0.06)' }} width={8} height={4} />
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);


// ═══════════════════════════════════════════════════════════
// COPY BUTTON — tiny inline icon button with feedback
// ═══════════════════════════════════════════════════════════

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  if (!value) return null;

  return (
    <Tooltip.Root delayDuration={300}>
      <Tooltip.Trigger asChild>
        <button
          onClick={handleCopy}
          className="inline-flex h-11 w-11 items-center justify-center rounded text-zinc-400/80 transition-colors hover:bg-white/[0.06] hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/5 sm:h-5 sm:w-5"
          aria-label={copied ? 'Copied' : `Copy ${label}`}
        >
          {copied
            ? <Check className="w-2.5 h-2.5 text-[var(--c-accent)]" />
            : <Copy className="w-2.5 h-2.5" />
          }
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          className={`z-[9999] px-2 py-1 rounded text-[10px] leading-4 font-mono shadow-md ${TEXT_TIER.secondary}`}
          style={{
            background: 'rgba(20,20,24,0.96)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
          }}
          sideOffset={4}
          side="top"
        >
          {copied ? 'Copied!' : `Copy ${label}`}
          <Tooltip.Arrow style={{ fill: 'rgba(255,255,255,0.06)' }} width={6} height={3} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};


// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

const ApplicationInsightsCard = ({
  insights,        // normalized form fields array
  coverage,        // number 0-100
  filledFields,    // number — filled field count
  totalFields,     // number — total field count
  onOpenFormData,  // ({ fieldKeyBase, contextId }) => void
  onViewPdf,       // () => void — open source PDF
  isLoading = false,
}) => {

  // ── Extract all signals ──────────────────────────────────
  const signals = useMemo(() => {
    if (!Array.isArray(insights) || insights.length === 0) return [];

    return KEY_SIGNALS.map((cfg) => {
      const result =
        extractCheckboxGroup(insights, cfg.fieldKeyBase, { aliases: cfg.fallbackKeys }) ||
        extractInsight(insights, cfg.fieldKeyBase, { aliases: cfg.fallbackKeys });
      const raw = result?.value || null;
      const displayValue = raw ? (cfg.format ? cfg.format(raw) : raw) : null;

      return {
        ...cfg,
        raw,
        displayValue,
        page: result?.page ?? null,
        question: result?.question ?? null,
        isNotable: displayValue ? cfg.notable(displayValue) : false,
      };
    });
  }, [insights]);

  const agent = useMemo(() => extractContact(insights, 'agent'), [insights]);
  const applicant = useMemo(() => extractContact(insights, 'applicant'), [insights]);
  const submissionRole = useMemo(
    () => extractInsight(insights, 'submission_role', { aliases: ['submitted_by', 'applicant_or_agent'] }),
    [insights]
  );
  const plans = useMemo(() => extractPlans(insights), [insights]);
  const [showMoreSignals, setShowMoreSignals] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const submittedByAgent = useMemo(() => {
    const role = clean(submissionRole?.value || '').toLowerCase();
    if (role.includes('agent')) return true;
    if (role.includes('applicant')) return false;
    return Boolean(agent);
  }, [submissionRole, agent]);

  const notableFlags = useMemo(() => buildNotableFlags(signals), [signals]);
  const keyInsights = useMemo(
    () => buildKeyInsights(signals, plans, submittedByAgent),
    [signals, plans, submittedByAgent]
  );
  const quickInsights = useMemo(() => {
    const notableLines = notableFlags.map((flag) => `${flag.label}: ${flag.value}.`);
    const merged = [...notableLines, ...keyInsights];
    return [...new Set(merged)].slice(0, 4);
  }, [notableFlags, keyInsights]);
  const visiblePlans = showAllPlans ? plans : plans.slice(0, 6);
  const hiddenPlanCount = Math.max(plans.length - 6, 0);
  const allSignalsCount = signals.length;

  const sourcePages = useMemo(() => {
    const pages = new Set();
    signals.forEach((s) => { if (s.page != null) pages.add(s.page); });
    return [...pages].sort((a, b) => a - b);
  }, [signals]);

  const extractionPercent = useMemo(() => {
    if (coverage != null && Number.isFinite(coverage)) return Math.max(0, Math.min(100, Math.round(coverage)));
    if (filledFields != null && totalFields != null && totalFields > 0) {
      return Math.max(0, Math.min(100, Math.round((filledFields / totalFields) * 100)));
    }
    return null;
  }, [coverage, filledFields, totalFields]);

  const extractionTooltip = useMemo(() => {
    const fieldsPart = filledFields != null && totalFields != null && totalFields > 0
      ? `${filledFields}/${totalFields} fields extracted`
      : 'Field coverage unavailable';
    const pagesPart = sourcePages.length > 0 ? `Pages ${sourcePages.join(', ')}` : 'Pages unavailable';
    return `${fieldsPart} • ${pagesPart}`;
  }, [filledFields, totalFields, sourcePages]);

  const hasAnySignals = signals.some((s) => s.displayValue);
  const hasData = hasAnySignals || agent || applicant || plans.length > 0;

  const handleChipClick = (fieldKeyBase) => {
    if (typeof onOpenFormData === 'function') {
      onOpenFormData({ fieldKeyBase });
    }
  };

  // ── Loading state ────────────────────────────────────────
  if (isLoading) {
    return (
      <motion.div
        className="p-4 rounded-lg bg-[var(--c-bg)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_10px_24px_rgba(0,0,0,0.2)]"
        style={{ border: '1px solid rgba(255,255,255,0.05)' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-32 rounded bg-white/10 animate-pulse" />
        </div>
        <div className="h-8 rounded-md bg-white/[0.04] animate-pulse mb-3" />
        <div className="h-10 rounded-md bg-white/[0.03] animate-pulse mb-3" />
        <div className="space-y-1.5 mb-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-7 rounded bg-white/[0.03] animate-pulse" style={{ animationDelay: `${i * 70}ms` }} />
          ))}
        </div>
        <div className="h-7 rounded-md bg-white/[0.03] animate-pulse" />
      </motion.div>
    );
  }

  // ── Empty state ──────────────────────────────────────────
  if (!hasData) {
    return (
      <motion.div
        className="p-4 rounded-lg bg-[var(--c-bg)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_10px_24px_rgba(0,0,0,0.2)]"
        style={{ border: '1px solid rgba(255,255,255,0.05)' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-[var(--c-text-secondary)]" />
            <span className="text-xs font-mono font-semibold uppercase tracking-wide leading-4 text-[var(--c-text-secondary)]">Application Insights</span>
          </div>
        </div>
        <p className={`text-sm leading-5 mb-3 ${TEXT_TIER.secondary}`}>
          No form insights available yet.
        </p>
        {typeof onViewPdf === 'function' && (
          <motion.button
            onClick={onViewPdf}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-md bg-[var(--c-surface-hover)] px-3 text-xs text-[var(--c-accent)] transition-colors hover:bg-[var(--c-surface)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
            style={{ border: '1px solid var(--c-border)' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ExternalLink className="w-3 h-3" />
            View original PDF
          </motion.button>
        )}
      </motion.div>
    );
  }

  // ── Full card ────────────────────────────────────────────
  return (
    <Tooltip.Provider delayDuration={180}>
      <motion.div
        className="relative rounded-lg bg-[var(--c-bg)] overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_10px_28px_rgba(0,0,0,0.24)]"
        style={{ border: '1px solid rgba(255,255,255,0.05)' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        whileHover={{ y: -2, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 12px 28px rgba(0,0,0,0.28)' }}
      >
        {/* Subtle top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, var(--c-accent) 30%, var(--c-accent) 70%, transparent 100%)',
            opacity: 0.24,
          }}
        />

        <div className="p-4 space-y-3">
          {/* ── Header ──────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSearch className="w-4 h-4 text-[var(--c-accent)]" />
              <span className="text-xs font-mono font-semibold uppercase tracking-wide leading-4 text-[var(--c-text-secondary)]">
                Application Insights
              </span>
              <span
                className="px-1.5 py-0.5 text-[9px] font-mono font-semibold rounded-full uppercase tracking-wide leading-4 text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)]"
                style={{
                  letterSpacing: '0.04em',
                }}
              >
                From Form
              </span>
              {extractionPercent != null && extractionPercent < 100 && (
                <Tooltip.Root delayDuration={200}>
                  <Tooltip.Trigger asChild>
                    <button
                      className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                      aria-label={extractionTooltip}
                      type="button"
                    >
                      <span className="relative h-1 w-7 overflow-hidden rounded-full bg-[var(--c-text-secondary)]/15">
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-[var(--c-text-secondary)]/50"
                          style={{ width: `${extractionPercent}%` }}
                        />
                      </span>
                      <span className="text-[9px] leading-4 font-mono text-[var(--c-text-secondary)]/70">
                        {extractionPercent}%
                      </span>
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className={`z-[9999] px-2 py-1 rounded text-[10px] leading-4 font-mono shadow-md ${TEXT_TIER.secondary}`}
                      style={{
                        background: 'rgba(20,20,24,0.96)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                      }}
                      sideOffset={4}
                      side="top"
                    >
                      {extractionTooltip}
                      <Tooltip.Arrow style={{ fill: 'rgba(255,255,255,0.06)' }} width={6} height={3} />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              )}
            </div>
            <motion.button
              onClick={() => typeof onOpenFormData === 'function' && onOpenFormData({})}
              className="inline-flex min-h-11 items-center gap-1 rounded-md bg-[var(--c-surface-hover)] px-3 text-xs text-[var(--c-accent)] transition-colors hover:bg-[var(--c-surface)] hover:text-[var(--c-accent)]/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
              style={{ border: '1px solid var(--c-border)' }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Open Form Data
              <ChevronRight className="w-3 h-3" />
            </motion.button>
          </div>

          {/* -- B) Key insights ------------------------------------ */}
          <div
            className="rounded-md px-3 py-2.5 bg-[var(--c-surface-hover)]"
          >
            <div className="text-sm leading-5 font-mono font-semibold uppercase tracking-wide text-[var(--c-accent)]">
              Key insights
            </div>
            <div className="mt-1.5 space-y-0.5">
              {quickInsights.map((insight, idx) => (
                <p key={idx} className={`text-[11px] leading-5 ${idx === 0 ? TEXT_TIER.primary : TEXT_TIER.secondary}`}>
                  {insight}
                </p>
              ))}
            </div>
          </div>

          {/* -- C) More insights ----------------------------------- */}
          <div
            className="rounded-md bg-[var(--c-surface-hover)] overflow-hidden border border-[var(--c-border)]"
          >
            <motion.button
              onClick={() => setShowMoreSignals((prev) => !prev)}
              className="flex min-h-11 w-full items-center justify-between px-3 text-left transition-colors hover:bg-[var(--c-surface)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)] sm:py-2.5"
              whileTap={{ scale: 0.995 }}
            >
              <span className="text-[10px] leading-4 font-mono font-semibold uppercase tracking-wide text-[var(--c-text-secondary)]">
                More insights ({allSignalsCount})
              </span>
              <ChevronDown className={`w-3 h-3 text-[var(--c-text-secondary)]/80 transition-transform ${showMoreSignals ? 'rotate-180' : ''}`} />
            </motion.button>

            <AnimatePresence initial={false}>
              {showMoreSignals && (
                <motion.div
                  className="px-3 pb-1.5 overflow-hidden"
                  initial={{ opacity: 0, height: 0, y: -6 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -4 }}
                  transition={{
                    height: { type: 'spring', stiffness: 280, damping: 28, mass: 0.75 },
                    opacity: { duration: 0.2 },
                    y: { type: 'spring', stiffness: 320, damping: 24 },
                  }}
                >
                  {signals.map((signal, idx) => (
                    <ChipTooltip key={signal.id} page={signal.page} question={signal.question}>
                      <motion.button
                        onClick={() => handleChipClick(signal.fieldKeyBase)}
                        className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-2 text-left hover:bg-[var(--c-surface)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                        style={idx > 0 ? { borderTop: '1px solid var(--c-border)' } : undefined}
                        initial={{ opacity: 0, y: -3 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.025, type: 'spring', stiffness: 300, damping: 24 }}
                        whileTap={{ scale: 0.998 }}
                      >
                        <span className="text-[10px] leading-4 uppercase tracking-wide font-semibold text-[var(--c-text-secondary)]">{signal.label}</span>
                        <span className={`text-xs leading-5 ${signal.isNotable ? 'text-[var(--c-accent)] font-semibold' : 'text-[var(--c-text-primary)] font-medium'}`}>
                          {signal.displayValue || 'Not stated'}
                        </span>
                      </motion.button>
                    </ChipTooltip>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* -- D) People - shared surface ------------------------- */}
          {(agent || applicant) && (
            <div>
              <div className="text-[10px] leading-4 font-mono font-semibold uppercase tracking-wide text-[var(--c-text-secondary)] pl-0.5 mb-1">
                People
              </div>
              <div
                className="rounded-md bg-[var(--c-surface-hover)] overflow-hidden border border-[var(--c-border)]"
              >
                <PeopleRow
                  role="Agent"
                  icon={Building}
                  name={agent?.name}
                  company={agent?.company}
                  email={agent?.email}
                  phone={agent?.phone}
                  allowCopy
                  showContact={false}
                />
                <div className="h-px bg-[var(--c-border)]" />
                <AgentContactRow
                  email={agent?.email}
                  phone={agent?.phone}
                />
                <div className="h-px bg-[var(--c-border)]" />
                <PeopleRow
                  role="Applicant"
                  icon={User}
                  name={applicant?.name}
                  company={applicant?.company}
                  email={applicant?.email}
                  phone={applicant?.phone}
                  fallbackContact="Contact via agent"
                />
              </div>
            </div>
          )}

          {/* -- E) Plans included ---------------------------------- */}
          {plans.length > 0 && (
            <div>
              <div className="text-[10px] leading-4 font-mono font-semibold uppercase tracking-wide text-[var(--c-text-secondary)] pl-0.5 mb-1">
                Plans included ({plans.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {visiblePlans.map((plan) => {
                  const theme = getPlanChipTheme(plan);
                  return (
                    <span
                      key={plan}
                      className={`inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[8px] leading-4 font-mono ${theme.textClass}`}
                      style={{
                        background: theme.bg,
                        border: `1px solid ${theme.border}`,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                      }}
                    >
                      <Layers className={`w-2 h-2 ${theme.iconClass}`} />
                      {plan}
                    </span>
                  );
                })}
                {hiddenPlanCount > 0 && !showAllPlans && (
                  <button
                    onClick={() => setShowAllPlans(true)}
                    className="inline-flex items-center px-1.5 py-px rounded-full text-[8px] leading-4 font-mono text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                    style={{ border: '1px solid var(--c-border)' }}
                  >
                    +{hiddenPlanCount} more
                  </button>
                )}
                {plans.length > 6 && showAllPlans && (
                  <button
                    onClick={() => setShowAllPlans(false)}
                    className="inline-flex items-center px-1.5 py-px rounded-full text-[8px] leading-4 font-mono text-[var(--c-text-secondary)] bg-[var(--c-surface-hover)] hover:bg-[var(--c-surface)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--c-border)]"
                    style={{ border: '1px solid var(--c-border)' }}
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </motion.div>
    </Tooltip.Provider>
  );
};


// ═══════════════════════════════════════════════════════════
// ------------------------------------------------------------
// PEOPLE ROW SUB-COMPONENT
// ------------------------------------------------------------

const PeopleRow = ({
  role,
  icon: Icon,
  name,
  company,
  email,
  phone,
  fallbackContact,
  allowCopy = false,
  showContact = true,
}) => {
  const normalizedName = clean(name);
  const normalizedCompany = clean(company);
  const displayName =
    normalizedName || (role === 'Applicant' ? normalizedCompany : '') || 'Not provided';
  const showCompanyChip = Boolean(normalizedCompany && normalizedCompany !== displayName);
  const hasDirectContact = email || phone;
  const showDirectContact = showContact && hasDirectContact;
  const showFallback = role === 'Applicant' && !hasDirectContact;

  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 min-h-[34px] ${allowCopy ? 'group/agent' : ''}`}>
      <Icon className="w-3 h-3 text-[var(--c-text-secondary)]/80 flex-shrink-0" />

      <span className="text-[10px] leading-4 font-mono font-semibold uppercase tracking-wide text-[var(--c-accent)] flex-shrink-0 w-[62px]">
        {role}
      </span>

      <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-1.5 flex-1 min-w-0">
        <span className="text-[12px] leading-5 text-[var(--c-text-primary)] font-medium truncate">
          {displayName}
        </span>
        {showCompanyChip && (
          <span className="inline-flex items-center rounded-full px-1.5 py-px text-[9px] leading-4 font-mono text-[var(--c-text-secondary)] bg-[var(--c-surface)] truncate">
            {normalizedCompany}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {showDirectContact && (
          <>
            {email && (
              <span className="hidden lg:inline-flex items-center gap-1 text-[10px] leading-4 text-[var(--c-text-secondary)]/80 max-w-[132px] truncate">
                <Mail className="w-2.5 h-2.5" />
                <span className="truncate">{email}</span>
              </span>
            )}
            {phone && (
              <span className="hidden lg:inline-flex items-center gap-1 text-[10px] leading-4 text-[var(--c-text-secondary)]/80 max-w-[112px] truncate">
                <Phone className="w-2.5 h-2.5" />
                <span className="truncate">{phone}</span>
              </span>
            )}
            {allowCopy && (
              <span className="inline-flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover/agent:opacity-100 group-focus-within/agent:opacity-100">
                <CopyButton value={email} label="email" />
                <CopyButton value={phone} label="phone" />
              </span>
            )}
          </>
        )}
        {showFallback && (
          <span className="text-[10px] leading-4 text-[var(--c-text-secondary)] italic">
            {fallbackContact || 'Contact via agent'}
          </span>
        )}
      </div>
    </div>
  );
};

const AgentContactRow = ({ email, phone }) => {
  const hasDirectContact = email || phone;

  return (
    <div className="group/agent-contact flex items-center gap-2 px-2.5 py-1.5 min-h-[30px] bg-[var(--c-surface)]">
      <Phone className="w-3 h-3 text-[var(--c-text-secondary)]/80 flex-shrink-0" />

      <span className="text-[10px] leading-4 font-mono font-semibold uppercase tracking-wide text-[var(--c-accent)] flex-shrink-0 w-[62px]">
        Contact
      </span>

      {hasDirectContact ? (
        <>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {email && (
              <span className="inline-flex items-center gap-1 text-[10px] leading-4 text-[var(--c-text-secondary)] min-w-0 truncate">
                <Mail className="w-2.5 h-2.5 text-[var(--c-text-secondary)]/80" />
                <span className="truncate">{email}</span>
              </span>
            )}
            {phone && (
              <span className="inline-flex items-center gap-1 text-[10px] leading-4 text-[var(--c-text-secondary)] min-w-0 truncate">
                <Phone className="w-2.5 h-2.5 text-[var(--c-text-secondary)]/80" />
                <span className="truncate">{phone}</span>
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover/agent-contact:opacity-100 group-focus-within/agent-contact:opacity-100">
            <CopyButton value={email} label="email" />
            <CopyButton value={phone} label="phone" />
          </span>
        </>
      ) : (
        <span className={`text-[10px] leading-4 ${TEXT_TIER.muted}`}>
          No direct agent contact provided.
        </span>
      )}
    </div>
  );
};


export default ApplicationInsightsCard;
